/**
 * Comments panel (F-55): right sidebar listing every thread on the board with
 * an open / resolved / mentions-me filter; clicking a thread pans the camera
 * to its pin and opens the thread popover. Orphaned threads stay accessible
 * here and are labeled "original location removed" with jump-to-last-
 * position (F-52 — the C4 degradation re-anchored them to the board at that
 * position, so the ordinary resolver finds them).
 *
 * "Mentions me" is the board-local half of F-110: the bell answers "what has
 * happened to me anywhere", this answers "where am I tagged on THIS board",
 * including threads that were resolved. It re-derives from message bodies
 * through the same directory the highlighter uses, so it can never disagree
 * with what the messages visibly say (docs/04 §2 — mentions are never stored).
 */
import { PanelResizeHandle } from "../../ui/PanelResizeHandle.js";
import { useDockedPanelWidth } from "../../ui/useDockedPanelWidth.js";
import type { CommentThreadInfo } from "@kola/shared";
import { boardToScreen } from "@kola/shared";
import { useState } from "react";
import { useStore } from "zustand";

import type { CanvasStore } from "../../core/canvas.js";
import { colorForName, currentUser, initialsFor } from "../../core/identity.js";
import type { BoardSession } from "../../core/session.js";
import { CloseButton } from "../../ui/CloseButton.js";
import { RowButton } from "../../ui/RowButton.js";
import { formatRelativeTime, resolveAnchorPosition } from "./anchoring.js";
import { elementRectLookup, useCommentThreads } from "./hooks.js";
import { threadMentionsMe, useMentionDirectory } from "./mentions.js";
import type { CommentsUiStore } from "./store.js";

type Filter = "open" | "resolved" | "mentions";

export interface CommentsPanelProps {
  session: BoardSession;
  canvas: CanvasStore;
  ui: CommentsUiStore;
}

export function CommentsPanel({ session, canvas, ui }: CommentsPanelProps) {
  const panelWidth = useDockedPanelWidth("comments", 280);
  const threads = useCommentThreads(session);
  const showResolved = useStore(ui, (s) => s.showResolved);
  const openThreadId = useStore(ui, (s) => s.openThreadId);
  const [filter, setFilter] = useState<Filter>("open");
  const directory = useMentionDirectory();
  const meId = currentUser().id;

  // Empty threads (concurrent-delete merge, mid-sweep — see the pins layer)
  // never reach the list or the counts.
  const listed = threads.filter((t) => t.messages.length > 0);
  // "Mentions me" deliberately ignores the resolved/open split: a thread
  // someone resolved is still the record of having been asked something.
  // Shared with the pin's tagged marker, so the board and the filter always
  // agree about which threads are addressed to the reader.
  const mentioned = listed.filter((t) => threadMentionsMe(t, directory, meId));
  const shown =
    filter === "mentions"
      ? mentioned
      : listed.filter((t) => (filter === "resolved" ? t.resolved : !t.resolved));
  const counts = {
    open: listed.reduce((n, t) => n + (t.resolved ? 0 : 1), 0),
    resolved: listed.reduce((n, t) => n + (t.resolved ? 1 : 0), 0),
    mentions: mentioned.length,
  };

  /** Jump the viewport to a thread's pin, then open its popover (F-55). */
  const jumpTo = (thread: CommentThreadInfo) => {
    const position = resolveAnchorPosition(
      thread.anchor,
      elementRectLookup(session),
      ui.getState().gridLayout,
    );
    if (position !== null) {
      // Center the pin: pan by the screen-space offset from its current
      // projection to the viewport center (pure camera math — view state).
      const { camera, viewport } = canvas.getState();
      const screen = boardToScreen(camera, position);
      canvas.getState().panBy(viewport.width / 2 - screen.x, viewport.height / 2 - screen.y);
    }
    ui.getState().openThread(thread.id);
  };

  const now = Date.now();

  return (
    <aside className="comments-panel" aria-label="Comments" style={{ width: panelWidth.width }}>
      <PanelResizeHandle
        label="Resize the comments panel"
        width={panelWidth.width}
        bounds={panelWidth.bounds}
        onResize={panelWidth.setWidth}
        onCommit={panelWidth.commit}
        onReset={panelWidth.reset}
      />
      <div className="comments-panel-head">
        <span className="comments-panel-title">Comments</span>
        <span className="spacer" />
        <CloseButton label="Close panel" onClick={() => ui.getState().setPanelOpen(false)} />
      </div>
      <div className="comments-panel-filters" role="tablist" aria-label="Filter threads">
        {(["open", "resolved", "mentions"] as const).map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={filter === f}
            className={`comments-filter${filter === f ? " active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f === "open"
              ? `Open (${counts.open})`
              : f === "resolved"
                ? `Resolved (${counts.resolved})`
                : `@me (${counts.mentions})`}
          </button>
        ))}
      </div>
      <label className="comments-panel-toggle">
        <input
          type="checkbox"
          checked={showResolved}
          onChange={(e) => ui.getState().setShowResolved(e.target.checked)}
        />
        Show resolved pins
      </label>
      <div className="comments-panel-list">
        {shown.length === 0 ? (
          <div className="comments-panel-empty">
            {filter === "open"
              ? "No open comments"
              : filter === "resolved"
                ? "No resolved comments"
                : "Nobody has @-tagged you on this board"}
          </div>
        ) : (
          shown.map((thread) => {
            const first = thread.messages[0];
            const author = first?.authorName ?? "?";
            const replies = thread.messages.length - 1;
            return (
              <RowButton
                key={thread.id}
                className={`comments-panel-item${thread.id === openThreadId ? " active" : ""}`}
                onActivate={() => jumpTo(thread)}
                title="Jump to pin"
              >
                <span className="cm-avatar" style={{ background: colorForName(author) }}>
                  {initialsFor(author)}
                </span>
                <span className="comments-item-main">
                  <span className="comments-item-head">
                    <span className="cm-author">{author}</span>
                    <span className="cm-time">
                      {first !== undefined ? formatRelativeTime(first.ts, now) : ""}
                    </span>
                  </span>
                  <span className="comments-item-snippet">{first?.body ?? ""}</span>
                  <span className="comments-item-meta">
                    {replies > 0 && <span>{replies === 1 ? "1 reply" : `${replies} replies`}</span>}
                    {thread.orphaned && (
                      <span className="comments-item-orphan">original location removed</span>
                    )}
                  </span>
                </span>
              </RowButton>
            );
          })
        )}
      </div>
    </aside>
  );
}
